import { NotFoundPageComponent } from '@jablotron/lib.spa-components';
import { captureException } from '@jablotron/lib.spa-sentry';
import { ErrorComponentProps, isNotFound, SearchParamError } from '@tanstack/react-router';
import { PathParamError } from '@tanstack/router-core';
import { Component, PropsWithChildren } from 'react';
import { GeneralError } from '../errors/GeneralError';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error | unknown;
}

interface Props extends Partial<ErrorComponentProps> {
  CustomErrorComponent?: React.FC;
}

export class ErrorBoundary extends Component<PropsWithChildren<Props>, ErrorBoundaryState> {
  constructor(props: PropsWithChildren<Props>) {
    super(props);
    this.state = { hasError: false };
  }

  public static getDerivedStateFromError(error: Error | unknown) {
    return { hasError: true, error };
  }

  public componentDidMount() {
    const { error, info } = this.props;
    if (error) {
      this.setState({ hasError: true, error });
      captureException(error, { data: { info } });
    }
  }

  public componentDidCatch(error: Error, info: React.ErrorInfo) {
    captureException(error, { data: { info } });
  }

  // eslint-disable-next-line @typescript-eslint/promise-function-async
  public render() {
    const { children, CustomErrorComponent = GeneralError } = this.props;

    if (this.state.hasError) {
      if (isNotFoundError(this.state.error)) {
        return <NotFoundPageComponent />;
      }

      return <CustomErrorComponent />;
    }

    return children;
  }
}

export const isNotFoundError = (error: unknown): boolean => {
  if (!error) {
    return false;
  }

  if (isNotFound(error) || error instanceof SearchParamError || error instanceof PathParamError) {
    return true;
  }

  return false;
};
